"use client";

import { useEffect, useState } from "react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
interface Stat {
  label: string;
  value: number;
  prefix: string;
  suffix: string;
  display: string;
  caption: string;
}
const stats: Stat[] = [{
  label: "TREES PRESERVED",
  value: 12400,
  prefix: "",
  suffix: "+",
  display: "12,400+",
  caption: "Healthy specimens saved through surgical pruning and care."
}, {
  label: "RESPONSE TIME",
  value: 24,
  prefix: "<",
  suffix: "hr",
  display: "<24hr",
  caption: "Average emergency response time for DFW storm damage."
}, {
  label: "ISA ARBORISTS",
  value: 12,
  prefix: "",
  suffix: "",
  display: "12",
  caption: "Certified specialists overseeing every residential project."
}, {
  label: "SAFETY RATING",
  value: 100,
  prefix: "",
  suffix: "%",
  display: "100%",
  caption: "Incident-free safety record across all Fort Worth operations."
}];
function useCountUp(target: number, isVisible: boolean, duration = 1200, decimals = 0) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!isVisible) return;
    let startTime: number | null = null;
    let raf = 0;
    const easeOutExpo = (t: number) => t === 1 ? 1 : 1 - Math.pow(2, -10 * t);
    const step = (timestamp: number) => {
      if (startTime === null) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const eased = easeOutExpo(progress);
      const current = target * eased;
      setValue(decimals > 0 ? Number(current.toFixed(decimals)) : Math.floor(current));
      if (progress < 1) {
        raf = requestAnimationFrame(step);
      } else {
        setValue(target);
      }
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target, isVisible, duration, decimals]);
  return value;
}
function StatBlock({
  stat,
  index,
  isVisible
}: {
  stat: Stat;
  index: number;
  isVisible: boolean;
}) {
  const decimals = stat.value % 1 !== 0 ? 1 : 0;
  const animated = useCountUp(stat.value, isVisible, 1200, decimals);
  const formatNumber = (n: number) => {
    if (stat.value >= 1000 && decimals === 0) {
      return n.toLocaleString("en-US");
    }
    if (decimals > 0) {
      return n.toFixed(1);
    }
    return n.toString();
  };
  const displayValue = isVisible ? `${stat.prefix}${formatNumber(animated)}${stat.suffix}` : `${stat.prefix}0${stat.suffix}`;
  return <div data-index={index} className="flex flex-col justify-between min-h-[320px] px-6 md:px-8 lg:px-10 py-10 lg:py-0">
      <div className="font-mono uppercase text-[11px] tracking-[0.2em] text-secondary">
        {stat.label}
      </div>

      <div className="font-default font-bold text-foreground leading-[0.9] my-8" style={{
      letterSpacing: "-0.04em"
    }}>
        <span className="text-primary block" style={{
        fontSize: "clamp(64px, 12vw, 180px)",
        fontVariantNumeric: "lining-nums"
      }}>
          {displayValue}
        </span>
      </div>

      <p className="font-mono text-xs leading-snug text-secondary max-w-[260px] uppercase tracking-wider">
        {stat.caption}
      </p>
    </div>;
}
export default function OurImpact() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    once: true,
    threshold: 0.25
  });
  return <section data-section-id="3216" id="our-impact" ref={ref} className="dark bg-background text-foreground relative overflow-hidden py-32 md:py-44 lg:py-[200px]">
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 flex items-center justify-center opacity-[0.08]">
        <svg viewBox="0 0 1200 400" className="w-full h-auto max-w-[1600px]" fill="none" stroke="currentColor" strokeWidth="1">
          <g>
            {}
            <rect x="540" y="170" width="120" height="60" />
            <circle cx="600" cy="200" r="18" />
            <circle cx="600" cy="200" r="6" />
            <line x1="540" y1="170" x2="660" y2="230" />
            <line x1="660" y1="170" x2="540" y2="230" />

            {}
            <line x1="555" y1="180" x2="300" y2="80" />
            <line x1="645" y1="180" x2="900" y2="80" />
            <line x1="555" y1="220" x2="300" y2="320" />
            <line x1="645" y1="220" x2="900" y2="320" />

            {}
            <circle cx="290" cy="80" r="40" />
            <circle cx="290" cy="80" r="28" />
            <circle cx="290" cy="80" r="8" />
            <circle cx="910" cy="80" r="40" />
            <circle cx="910" cy="80" r="28" />
            <circle cx="910" cy="80" r="8" />
            <circle cx="290" cy="320" r="40" />
            <circle cx="290" cy="320" r="28" />
            <circle cx="290" cy="320" r="8" />
            <circle cx="910" cy="320" r="40" />
            <circle cx="910" cy="320" r="28" />
            <circle cx="910" cy="320" r="8" />

            {}
            <ellipse cx="290" cy="80" rx="60" ry="6" />
            <ellipse cx="290" cy="80" rx="6" ry="60" />
            <ellipse cx="910" cy="80" rx="60" ry="6" />
            <ellipse cx="910" cy="80" rx="6" ry="60" />
            <ellipse cx="290" cy="320" rx="60" ry="6" />
            <ellipse cx="290" cy="320" rx="6" ry="60" />
            <ellipse cx="910" cy="320" rx="60" ry="6" />
            <ellipse cx="910" cy="320" rx="6" ry="60" />

            {}
            <rect x="585" y="155" width="30" height="20" />
            <circle cx="600" cy="165" r="5" />

            {}
            <line x1="290" y1="40" x2="910" y2="40" strokeDasharray="4 4" />
            <line x1="290" y1="35" x2="290" y2="45" />
            <line x1="910" y1="35" x2="910" y2="45" />
            <line x1="40" y1="80" x2="40" y2="320" strokeDasharray="4 4" />
            <line x1="35" y1="80" x2="45" y2="80" />
            <line x1="35" y1="320" x2="45" y2="320" />
          </g>
        </svg>
      </div>

      <div className="relative z-10 max-w-[1600px] mx-auto px-6 md:px-10">
        <div className="flex items-center justify-between mb-16 md:mb-24">
          <div className="font-mono text-[12px] uppercase tracking-[0.2em] text-secondary">
            [ CANOPY METRICS / DFW REGION ]
          </div>

          <div className="flex items-center gap-2 font-mono text-[12px] uppercase tracking-[0.2em] text-foreground">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-lg bg-primary opacity-75"></span>
              <span className="relative inline-flex h-2 w-2 rounded-lg bg-primary"></span>
            </span>
            <span>ACTIVE OPERATIONS</span>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 divide-y divide-border md:divide-y-0 md:[&>*:nth-child(-n+2)]:border-b md:[&>*:nth-child(-n+2)]:border-border md:[&>*:nth-child(odd)]:border-r md:[&>*:nth-child(odd)]:border-border lg:[&>*]:border-b-0 lg:[&>*]:border-r lg:[&>*]:border-border lg:[&>*:last-child]:border-r-0">
          {stats.map((stat, i) => <StatBlock key={stat.label} stat={stat} index={i} isVisible={isVisible} />)}
        </div>
      </div>
    </section>;
}